import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Manager, Target, Popper } from "react-popper";
import Typography from "material-ui/Typography";
import ClickAwayListener from "material-ui/utils/ClickAwayListener";
import Collapse from "material-ui/transitions/Collapse";
import Portal from "material-ui/Portal";
import { MenuItem, MenuList } from "material-ui/Menu";
import { withStyles } from "material-ui/styles";
import IconButton from "material-ui/IconButton";
import Button from "material-ui/Button";

import Bell from "../../Icons/Bell";
import { NotificationType } from "../../../constants/ComponentTypes";
import { containsHtmlElement, convertUrlToLink } from "../../../utils/utils";

import Loader from "../../Parts/Loader";
import * as moment from "moment";

const styles = () => ({
  root: {
    display: "flex",
  },
  popperClose: {
    pointerEvents: "none",
  },
  btn: {
    fontSize: 13,
  },

  item: {
    borderBottom: "1px solid #D8DBDF",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    padding: "15px",
    height: "unset",
    minWidth: "50px",
    "&:hover": {
      backgroundColor: "rgba(26,101,255,.05)",
    },
  },

  lastItem: {
    border: "none",
  },

  iconButton: {
    height: "24px",
    width: "24px",
    margin: "8px 8px 8px 16px",
  },

  dot: {
    background: "#F03A47",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    position: "absolute",
    top: -5,
    left: 0,
    boxShadow:
      "0 12px 28px 0 rgba(0,0,0,0.15),0 2px 4px 0 rgba(0,0,0,0.15),inset 0 0 0 1px rgba(0,0,0,0.15)",
  },

  unreadDotContainer: {
    minWidth: "12px",
  },

  unreadDot: {
    background: "#1A65FF",
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    display: "block",
    boxShadow:
      "0 12px 28px 0 rgba(0,0,0,0.15),0 2px 4px 0 rgba(0,0,0,0.15),inset 0 0 0 1px rgba(0,0,0,0.15)",
  },

  notificationContainer: {
    margin: 12,
    minWidth: "360px",
    width: "360px",
    background: "#FFFFFF",
    border: "1px solid #D8DBDF",
    boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.15)",
    borderRadius: "4px",
    maxHeight: "650px",
    overflow: "scroll",
  },

  notificationHead: {
    padding: "15px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  button: {
    color: "#1A65FF",
    fontSize: "15px",
    fontWeight: "bold",
    width: "unset",
    height: "unset",
    minHeight: "unset",
    padding: 0,
    "&:hover": {
      textDecoration: "underline",
      backgroundColor: "unset !important",
    },
  },

  messageContainer: {
    width: "100%",
  },

  message: {
    marginBottom: "8px",
    fontSize: "14px",
    whiteSpace: "normal",
    "& p": {
      padding: 0,
      margin: 0,
    },
  },

  messageBody: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "pre-wrap",
    display: "-webkit-box",
    "-webkit-line-clamp": 3,
    "-webkit-box-orient": "vertical",
  },
});

class NotificationBell extends React.Component {
  state = {
    open: false,
    showDot: false,
  };

  componentDidMount() {
    this.setState({
      showDot: this.hasUnreadNotifications(this.props.notifications),
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.notifications !== this.props.notifications) {
      this.setState({
        showDot: this.hasUnreadNotifications(this.props.notifications),
      });
    }
  }

  hasUnreadNotifications = (notifications) => {
    if (notifications.length > 0) {
      let unread_notifications = this.props.notifications.filter(
        (item) => !item.read_flag
      );
      if (!!unread_notifications && unread_notifications.length > 0) {
        return true;
      }
    }

    return false;
  };

  handleToggle = () => {
    this.setState({
      open: !this.state.open,
    });
  };

  handleClose = (event) => {
    if (this.target.contains(event.target)) {
      return;
    }

    this.setState({ open: false });
  };

  handleClick = (item) => {
    const dayCountAtWeek = 7;
    return () => {
      if (
        item.type === NotificationType.EXE_FILTER_NEW_EXE &&
        item.executive_list_filter !== null
      ) {
        this.props.postNotificationsMark(item.id, { read_flag: 1 });
        this.props.goTo("/executive-list", {
          filter: {
            ...item.executive_list_filter.filter,
            created_at_from: moment().subtract(dayCountAtWeek, "day").format("YYYY-MM-DD"),
          },
          query: {},
        });
        this.setState({ open: false });
      } else if (item.type === NotificationType.SYSTEM_NOTIF) {
        this.props.postNotificationsMark(item.id, { read_flag: 1 });
        this.props.toggleSystemNotificationDialog(item);
        this.setState({ open: false });
      } else if (item.type === NotificationType.RECOMMEND_RELOCATION_PERSONNEL) {
        this.props.postNotificationsMark(item.id, { read_flag: 1 });
        this.props.toggleRecommendRelocationPersonnelNotificationDialog(item);
        this.setState({ open: false });
      } else {
        this.setState({ open: false });
      }
    };
  };

  createMenuItems = () => {
    const { classes } = this.props;

    let menuItems = [];
    if (this.props.isLoading) {
      menuItems.push(
        <MenuItem key={0} className={classes.item} style={{ border: "none" }}>
          <div
            style={{
              transform: "translateY(10px)",
              height: "30px",
              width: "80px",
            }}
          >
            <Loader type="dark" />
          </div>
        </MenuItem>
      );
    } else if (this.props.notifications.length === 0) {
      menuItems.push(
        <MenuItem key={0} className={classes.item} style={{ border: "none" }}>
          <Typography variant="body2" style={{ marginBottom: "5px" }}>
            通知がありません。
          </Typography>
        </MenuItem>
      );
    } else {
      this.props.notifications.forEach((item, i) => {
        const messageHead = convertUrlToLink(item.message_head);
        let messageBody;
        if (item.type === NotificationType.RECOMMEND_RELOCATION_PERSONNEL) {
          messageBody = item.message_body;
        } else {
          messageBody = item.message_body ? item.type + convertUrlToLink(item.message_body) : '';
        }

        menuItems.push(
          <MenuItem
            onClick={this.handleClick(item)}
            key={i}
            className={classNames(
              classes.item,
              i === this.props.notifications.length - 1 ? classes.lastItem : ""
            )}
          >
            <div className={classes.messageContainer}>
              <div className={classes.message}>
                {containsHtmlElement(messageHead) ? (
                  <span
                    dangerouslySetInnerHTML={{ __html: messageHead }}
                  />
                ) : (
                  <p>{item.message_head}</p>
                )}
              </div>

              {!!item.message_body && (
                <div className={classes.message}>
                  {containsHtmlElement(messageBody) ? (
                    <span
                      dangerouslySetInnerHTML={{ __html: messageBody }}
                    />
                  ) : (
                    <p className={classes.messageBody}>{item.message_body}</p>
                  )}
                </div>
              )}

              <Typography variant="body1">
                {moment(item.created_at).format("YYYY年MM月DD日")}
              </Typography>
            </div>
            <div className={classes.unreadDotContainer}>
              {!item.read_flag && <span className={classes.unreadDot} />}
            </div>
          </MenuItem>
        );
      });
    }
    return menuItems;
  };

  render() {
    const { classes } = this.props;
    const { open } = this.state;

    return (
      <div className={classes.root}>
        <Manager>
          <Target>
            <div
              ref={(node) => {
                this.target = node;
              }}
            >
              <IconButton
                aria-owns={open ? "menu-list-collapse" : null}
                aria-haspopup="true"
                className={classes.iconButton}
                onClick={this.handleToggle}
              >
                <div
                  className={classes.dot}
                  style={{
                    visibility: this.state.showDot ? "visible" : "hidden",
                  }}
                ></div>
                <Bell
                  color={this.props.type === "dark" ? "#FFFFFF" : "#71737B"}
                  width={20}
                  height={20}
                />
              </IconButton>
            </div>
          </Target>
          <Portal>
            <Popper
              placement="bottom-end"
              eventsEnabled={open}
              className={classNames({ [classes.popperClose]: !open })}
              style={{ top: 5, zIndex: 1000 }}
            >
              <ClickAwayListener onClickAway={this.handleClose}>
                <Collapse
                  in={open}
                  id="menu-list-collapse"
                  style={{ transformOrigin: "0 0 0" }}
                >
                  <div className={classes.notificationContainer}>
                    <div className={classes.notificationHead}>
                      <Typography variant="display1">通知</Typography>
                      <Button
                        color="primary"
                        className={classes.button}
                        onClick={() => this.props.goTo("/notifications")}
                      >
                        すべてを見る
                      </Button>
                    </div>

                    <MenuList role="menu" style={{ padding: 0 }}>
                      {this.createMenuItems()}
                    </MenuList>
                  </div>
                </Collapse>
              </ClickAwayListener>
            </Popper>
          </Portal>
        </Manager>
      </div>
    );
  }
}

NotificationBell.propTypes = {
  classes: PropTypes.object.isRequired,
  notifications: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  type: PropTypes.string,
  goTo: PropTypes.func.isRequired,
  postNotificationsMark: PropTypes.func.isRequired,
  toggleSystemNotificationDialog: PropTypes.func.isRequired,
  toggleRecommendRelocationPersonnelNotificationDialog: PropTypes.func.isRequired,
};

NotificationBell.defaultProps = {
  isLoading: false,
  notifications: [],
};

export default withStyles(styles)(NotificationBell);



// WEBPACK FOOTER //
// ./src/components/common/NotificationBell/NotificationBell.jsx